import React, { useCallback, useEffect, useRef, useState } from 'react';
import { FileText, LogOut } from 'lucide-react';
import { termsApi } from '../services/termsService';
import { EutcContent } from '../types/terms';
import { Button } from './ui/button';

interface EutcAcceptanceModalProps {
  /** Called after POST /api/auth/accept-eutc succeeds. */
  onAccepted: () => void;
  /** Logs the user out via the app's existing logout flow. */
  onLogout: () => void;
}

// How close to the bottom (in px) counts as "read to the end" — absorbs
// sub-pixel rounding of scrollTop on zoomed / high-DPI displays.
const SCROLL_BOTTOM_TOLERANCE_PX = 8;

const TERMS_LOAD_ERROR_MESSAGE =
  'We could not load the terms and conditions. Please check your connection and try again.';
const ACCEPT_ERROR_MESSAGE =
  'We could not record your acceptance. Please try again.';

/**
 * Blocking, non-dismissable gate shown after login until the user accepts the
 * current End User Terms and Conditions. "I Agree" stays disabled until the
 * user has scrolled the terms to the bottom (or the content fits without
 * scrolling). The only other way out is logging out.
 */
const EutcAcceptanceModal: React.FC<EutcAcceptanceModalProps> = ({
  onAccepted,
  onLogout,
}) => {
  const [eutc, setEutc] = useState<EutcContent | null>(null);
  const [isLoadingTerms, setIsLoadingTerms] = useState(true);
  const [termsLoadError, setTermsLoadError] = useState<string | null>(null);
  const [hasScrolledToBottom, setHasScrolledToBottom] = useState(false);
  const [isAccepting, setIsAccepting] = useState(false);
  const [acceptError, setAcceptError] = useState<string | null>(null);
  const [loadAttempt, setLoadAttempt] = useState(0);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let isCancelled = false;

    const loadTerms = async () => {
      setIsLoadingTerms(true);
      setTermsLoadError(null);
      try {
        const response = await termsApi.fetchTerms();
        if (isCancelled) {
          return;
        }
        if (response?.success && response?.data?.eutc) {
          setEutc(response.data.eutc);
        } else {
          setTermsLoadError(TERMS_LOAD_ERROR_MESSAGE);
        }
      } catch {
        if (!isCancelled) {
          setTermsLoadError(TERMS_LOAD_ERROR_MESSAGE);
        }
      } finally {
        if (!isCancelled) {
          setIsLoadingTerms(false);
        }
      }
    };

    loadTerms();
    return () => {
      isCancelled = true;
    };
  }, [loadAttempt]);

  const checkScrollPosition = useCallback(() => {
    const container = scrollContainerRef.current;
    if (!container) {
      return;
    }
    const distanceFromBottom =
      container.scrollHeight - container.scrollTop - container.clientHeight;
    if (distanceFromBottom <= SCROLL_BOTTOM_TOLERANCE_PX) {
      setHasScrolledToBottom(true);
    }
  }, []);

  // Content that does not overflow must enable "I Agree" without scrolling.
  useEffect(() => {
    if (eutc) {
      checkScrollPosition();
    }
  }, [eutc, checkScrollPosition]);

  const handleAccept = async () => {
    setIsAccepting(true);
    setAcceptError(null);
    try {
      const response = await termsApi.acceptEutc();
      if (response?.success) {
        onAccepted();
      } else {
        setAcceptError(response?.message || ACCEPT_ERROR_MESSAGE);
      }
    } catch (error: any) {
      setAcceptError(error?.message || ACCEPT_ERROR_MESSAGE);
    } finally {
      setIsAccepting(false);
    }
  };

  const isAgreeDisabled =
    !eutc || isLoadingTerms || !!termsLoadError || !hasScrolledToBottom || isAccepting;

  return (
    <div
      className="modal-backdrop"
      role="dialog"
      aria-modal="true"
      aria-labelledby="eutc-modal-title"
    >
      <div className="modal-panel max-w-2xl w-full max-h-[85vh] flex flex-col">
        {/* Header */}
        <div className="border-b border-hairline p-6">
          <div className="flex items-center gap-3">
            <FileText className="w-6 h-6 text-signature" aria-hidden="true" />
            <div>
              <h2 id="eutc-modal-title" className="heading-3">
                {eutc?.title || 'End User Terms and Conditions'}
              </h2>
              {eutc?.lastUpdated && (
                <p className="text-sm text-ink-muted mt-1">
                  Last updated: {eutc.lastUpdated}
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Scrollable terms body */}
        <div
          ref={scrollContainerRef}
          onScroll={checkScrollPosition}
          data-testid="eutc-scroll-container"
          className="flex-1 overflow-y-auto p-6 space-y-4"
        >
          {isLoadingTerms && (
            <p className="text-sm text-ink-secondary" role="status">
              Loading terms and conditions…
            </p>
          )}

          {!isLoadingTerms && termsLoadError && (
            <div className="space-y-3">
              <p className="text-sm text-error" role="alert">
                {termsLoadError}
              </p>
              <Button
                type="button"
                variant="primary"
                onClick={() => setLoadAttempt((attempt) => attempt + 1)}
              >
                Retry
              </Button>
            </div>
          )}

          {!isLoadingTerms && !termsLoadError && eutc && (
            <>
              {(eutc.sections?.length ?? 0) > 0 ? (
                eutc.sections?.map((section, index) => (
                  <div key={index} className="mb-4">
                    {section?.heading && (
                      <p className="text-sm font-bold text-ink mb-1">
                        {section.heading}
                      </p>
                    )}
                    <p className="text-sm text-ink-secondary leading-relaxed whitespace-pre-line">
                      {section?.body}
                    </p>
                  </div>
                ))
              ) : (
                <p className="text-sm text-ink-secondary">
                  No terms content is available right now. Please contact your
                  administrator.
                </p>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-hairline p-4 bg-surface-subtle rounded-b-lg">
          {acceptError && (
            <p className="text-sm text-error mb-3" role="alert">
              {acceptError}
            </p>
          )}
          <div className="flex items-center justify-between gap-4">
            <Button
              type="button"
              variant="tertiary"
              onClick={onLogout}
              className="gap-2"
            >
              <LogOut className="w-4 h-4" aria-hidden="true" />
              Log out
            </Button>
            <div className="flex items-center gap-3">
              {!hasScrolledToBottom && !isLoadingTerms && !termsLoadError && (
                <span className="text-xs text-ink-muted">
                  Scroll to the bottom to enable "I Agree"
                </span>
              )}
              <Button
                type="button"
                variant="primary"
                onClick={handleAccept}
                disabled={isAgreeDisabled}
              >
                {isAccepting ? 'Submitting…' : 'I Agree'}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EutcAcceptanceModal;
